import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import SectionReveal from "@/components/SectionReveal";
import SEO, { SITE } from "@/components/SEO";
import { posts, CATEGORIES } from "@/blog/posts";

const sortedPosts = [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));

const BlogIndex = () => {
  const featured = sortedPosts[0];
  const rest = sortedPosts.slice(1);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The China Explorer Journal",
    description:
      "Travel guides, business briefings and cultural insights for international travellers to China.",
    url: `${SITE.url}/blog`,
    publisher: {
      "@type": "Organization",
      name: "The China Explorer",
      url: SITE.url,
    },
    blogPost: sortedPosts.slice(0, 10).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE.url}/blog/${p.category}/${p.slug}`,
      datePublished: p.date,
      image: `${SITE.url}${p.cover}`,
      author: { "@type": "Person", name: p.author },
    })),
  };

  return (
    <div className="App min-h-screen bg-[#F7F5F0] text-[#121212] font-body antialiased">
      <SEO
        title="The China Explorer Journal — Travel, Business & Culture Insights"
        description="Long-form travel guides, business briefings and cultural insights for international travellers to China. From Beijing itineraries to Canton Fair tips."
        path="/blog"
        keywords={[
          "china travel blog",
          "china travel guides",
          "business travel china",
          "factory visits china",
          "chinese culture",
          "mandarin learning",
          "china visa updates",
        ]}
        jsonLd={jsonLd}
      />
      <Header />

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#0E2A5C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-5">
            ✦ The China Explorer Journal
          </p>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] max-w-4xl">
            Field notes from the world&apos;s most fascinating country.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
            Travel guides, business briefings, cultural deep-dives and visa updates — written by our team on the ground across Beijing, Shanghai, Shenzhen and Guangzhou.
          </p>
        </div>
      </section>

      {/* CATEGORY PILLS */}
      <section className="bg-[#EAE6DF] py-10 border-b border-black/5 sticky top-20 z-30 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap gap-2.5">
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/blog/${c.slug}`}
              data-testid={`blog-cat-${c.slug}`}
              className="px-4 py-2 text-xs md:text-sm uppercase tracking-[0.15em] font-semibold border border-[#121212]/15 text-[#121212] hover:text-white transition-all"
              style={{ borderRadius: 9999 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = c.color; e.currentTarget.style.borderColor = c.color; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = "rgba(18,18,18,0.15)"; }}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="bg-[#F7F5F0] py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <SectionReveal>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">✦ Featured</p>
              <Link to={`/blog/${featured.category}/${featured.slug}`} className="grid md:grid-cols-2 gap-10 group" data-testid="blog-featured">
                <div className="relative aspect-[5/4] overflow-hidden">
                  <img src={featured.cover} alt={featured.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                </div>
                <div className="flex flex-col justify-center">
                  <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C62828]">
                    {CATEGORIES.find((c) => c.slug === featured.category)?.name}
                  </p>
                  <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05] tracking-tight group-hover:text-[#C62828] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-5 text-base md:text-lg text-[#4A4A4A] leading-relaxed">{featured.excerpt}</p>
                  <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#4A4A4A]">
                    {featured.author} · {new Date(featured.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {featured.readMinutes} min read
                  </p>
                  <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#C62828]">
                    Read article <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </SectionReveal>
          </div>
        </section>
      )}

      {/* LIST */}
      <section className="bg-[#F7F5F0] pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionReveal className="max-w-3xl mb-12">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">✦ Latest articles</p>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight leading-[1.05]">Read, plan, then go.</h2>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((p, i) => (
              <BlogCard key={p.slug} post={p} delay={i * 0.05} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <AIChat />
    </div>
  );
};

export const BlogCard = ({ post, delay = 0 }) => {
  const cat = CATEGORIES.find((c) => c.slug === post.category);
  return (
    <SectionReveal delay={delay}>
      <Link
        to={`/blog/${post.category}/${post.slug}`}
        data-testid={`blog-card-${post.slug}`}
        className="group block border border-[#EAE6DF] bg-white hover:border-[#C62828]/30 hover:-translate-y-1 transition-all"
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={post.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" loading="lazy" />
        </div>
        <div className="p-6">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: cat?.color }}>
            {cat?.name}
          </p>
          <h3 className="mt-3 font-heading text-xl leading-tight text-[#121212] group-hover:text-[#C62828] transition-colors">
            {post.title}
          </h3>
          <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed line-clamp-3">{post.excerpt}</p>
          <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A] flex items-center gap-2">
            <BookOpen size={11} /> {post.readMinutes} min read · {new Date(post.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </p>
        </div>
      </Link>
    </SectionReveal>
  );
};

export default BlogIndex;
